import { router } from '@inertiajs/react'

// Inertia's router cancels whatever visit is in flight as soon as a new one
// starts. Rock edits are background saves — an inline due date, a percent
// slider, a planner field autosave, all while a live meeting reloads its props
// every 10 seconds — so firing one visit per edit silently aborts the previous
// one: onSuccess/onError never run and the value never reaches the server.
//
// Every rock write therefore goes through this one serialized queue: a single
// visit at a time, the rest waiting, pumped from onFinish. Writes for the same
// rock that arrive while one is waiting are merged into it (field maps, so a
// due-date edit and a percent edit become one request) and all of their
// callbacks are kept — dropping the older subscriber would strand its UI on
// "Saving…".

export type RockSaveErrors = Record<string, string>

export interface RockSaveSubscriber {
  onSuccess?: () => void
  onError?: (errors: RockSaveErrors, message: string) => void
}

export type RockSaveFields = Record<string, string | number | boolean | null>

interface QueuedSave {
  key: string
  method: 'patch' | 'post' | 'delete'
  url: string
  /** Mergeable rock field map, sent as `{ rock: fields }`. PATCH only. */
  fields: RockSaveFields | null
  /** Verbatim request body, used by the non-rock writes that share the queue. */
  body: Record<string, unknown> | null
  only: string[]
  subscribers: RockSaveSubscriber[]
  retried: boolean
}

const CANCELLED_MESSAGE = "Couldn't save — the request was interrupted. Try again."
// A visit that never reports back must not wedge every later rock save.
const STALLED_VISIT_MS = 20000

const pending: QueuedSave[] = []
let inFlight = false
let postSeq = 0

/** Test seam: drops anything queued so module state can't leak between cases. */
export function resetRockSaveQueue(): void {
  pending.length = 0
  inFlight = false
  postSeq = 0
}

export function firstRockErrorMessage(errors: unknown): string {
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors as Record<string, unknown>)[0]
    if (typeof first === 'string' && first.length > 0) return first
    if (Array.isArray(first) && typeof first[0] === 'string' && first[0].length > 0) return first[0]
  }
  return "Couldn't save changes."
}

/**
 * Queue a PATCH of one or more rock fields. `only` names the page props to
 * reload — both the Rocks page and the meeting page expose the rock list as
 * `rocks`, so the default works on either screen.
 */
export function queueRockUpdate(
  teamId: number,
  rockId: number,
  fields: RockSaveFields,
  subscriber: RockSaveSubscriber = {},
  only: string[] = ['rocks'],
): void {
  const key = `rock:${teamId}:${rockId}`
  const last = pending[pending.length - 1]
  const waiting = last?.key === key && last.fields ? last : undefined

  if (waiting) {
    Object.assign(waiting.fields!, fields)
    waiting.subscribers.push(subscriber)
  } else {
    pending.push({
      key,
      method: 'patch',
      url: `/teams/${teamId}/rocks/${rockId}`,
      fields: { ...fields },
      body: null,
      only,
      subscribers: [subscriber],
      retried: false,
    })
  }

  pump()
}

/**
 * Queue the "Add to The List" create raised from a Rock Review row (task
 * #798). It shares this queue rather than firing its own Inertia visit: a
 * create alongside a rock autosave would cancel whichever of the two was
 * already in flight. The partial reload asks for `topics` so The List picks
 * the new topic up without leaving Rock Review, plus `flash`.
 *
 * Never merged and never retried: each submission is its own topic ("Save
 * and Create Another" must not fold a second topic into a first still
 * waiting), and re-POSTing a create the server may already have accepted
 * would duplicate it.
 */
export function queueMeetingTopicCreate(
  meetingId: number,
  body: Record<string, unknown>,
  subscriber: RockSaveSubscriber = {},
): void {
  postSeq += 1
  pending.push({
    key: `topic:create:${postSeq}`,
    method: 'post',
    url: `/meetings/${meetingId}/topics${window.location.search}`,
    fields: null,
    body,
    only: ['topics', 'flash'],
    subscribers: [subscriber],
    retried: true,
  })

  pump()
}

export function queueMilestoneMutation(
  method: 'post' | 'patch' | 'delete',
  url: string,
  body: Record<string, unknown>,
  subscriber: RockSaveSubscriber = {},
): void {
  pending.push({
    key: `milestone:${++postSeq}`, method, url, body, fields: null,
    only: ['rocks'], subscribers: [subscriber], retried: true,
  })
  pump()
}

function pump(): void {
  if (inFlight) return
  const item = pending.shift()
  if (!item) return

  inFlight = true
  let settled = false
  let released = false
  let watchdogId: number | undefined

  const release = () => {
    if (released) return
    released = true
    if (watchdogId !== undefined) window.clearTimeout(watchdogId)
    inFlight = false
    pump()
  }

  watchdogId = window.setTimeout(() => {
    if (!settled) {
      settled = true
      item.subscribers.forEach(s => s.onError?.({}, CANCELLED_MESSAGE))
    }
    release()
  }, STALLED_VISIT_MS)

  const body = item.fields ? { rock: item.fields } : (item.body ?? {})
  const options = {
    preserveScroll: true,
    preserveState: true,
    only: item.only,
    onSuccess: () => {
      if (settled) return
      settled = true
      item.subscribers.forEach(s => s.onSuccess?.())
    },
    onError: (errors: unknown) => {
      if (settled) return
      settled = true
      const message = firstRockErrorMessage(errors)
      item.subscribers.forEach(s => s.onError?.(errors as RockSaveErrors, message))
    },
    onFinish: () => {
      if (!settled) {
        // A cancelled visit never reaches onSuccess or onError. That normally
        // means some other visit (a navigation, the meeting heartbeat) started
        // on top of ours, so retry once before calling it a failure — never
        // let a cancellation pass for a save. Creates opt out of the retry
        // (they are queued pre-retried) so an accepted POST isn't repeated.
        if (!item.retried) {
          item.retried = true
          pending.unshift(item)
        } else {
          item.subscribers.forEach(s => s.onError?.({}, CANCELLED_MESSAGE))
        }
      }
      release()
    },
  }

  if (item.method === 'post') router.post(item.url, body, options)
  else if (item.method === 'delete') router.delete(item.url, options)
  else router.patch(item.url, body, options)
}
